import { useCallback, useMemo } from 'react';
import { useAppSelector } from '../../../stores/redux.store';
import {
  EventAttributes,
  useAnalyticsClient,
} from '../../../utilities/analytics/analytics-web-client';
import { useSocialLoginHandlers } from '../../../utilities/hooks/useLoginHandlers';
import { redirectToGoogleLogin } from '../../../utilities/social-login';
import { pageId } from '../constants';
import { useRegisterComponent } from '../../orchestrator';
import { getCurrentCloudType } from '../../../utilities/env';

type SocialSignupSupported = {
  envSupportsSocialSignup: true;
  handleGoogleLogin: (analyticsAttributes?: EventAttributes) => void;
  handleAppleLogin: () => void;
  handleMicrosoftLogin: () => void;
  handleSlackLogin: () => void;
};

type SocialSignupNotSupported = {
  envSupportsSocialSignup: false;
  handleGoogleLogin: null;
  handleAppleLogin: null;
  handleMicrosoftLogin: null;
  handleSlackLogin: null;
};

export const useSocialSignup = (): SocialSignupSupported | SocialSignupNotSupported => {
  useRegisterComponent(useSocialSignup.name);
  const analyticsClient = useAnalyticsClient();

  const handleGoogleLogin = useCallback(
    (analyticsAttributes: EventAttributes = {}) => {
      analyticsClient.buttonClickedEvent(pageId, 'googleLoginButton', analyticsAttributes);

      redirectToGoogleLogin({
        location: window.location,
        source: pageId,
      });
    },
    [analyticsClient]
  );

  const auth0Config = useAppSelector(state => state.appConfig?.auth0Config);
  const csrfToken = useAppSelector(state => state.csrfToken);

  const { handleAppleLogin, handleMicrosoftLogin, handleSlackLogin } = useSocialLoginHandlers({
    pageId,
    redirectParams: {
      location: window.location,
      auth0Config,
      csrfToken,
      source: pageId,
    },
  });

  const { isEmbedded } = useAppSelector(state => state.microbranding);
  const isCommercial = getCurrentCloudType() === 'commercial';

  const envSupportsSocialSignup = useMemo(
    () => !isEmbedded && isCommercial,
    [isEmbedded, isCommercial]
  );

  if (!envSupportsSocialSignup) {
    return {
      envSupportsSocialSignup: false,
      handleGoogleLogin: null,
      handleAppleLogin: null,
      handleMicrosoftLogin: null,
      handleSlackLogin: null,
    };
  }

  return {
    envSupportsSocialSignup: true,
    handleGoogleLogin,
    handleAppleLogin,
    handleMicrosoftLogin,
    handleSlackLogin,
  };
};
